import { useEffect, useState } from 'react';
import { FilePlus2, Trash2, Clock, Fuel, Zap, ShieldAlert, Siren, Users, BarChart3, PlugZap, Wrench, Sparkles, ArrowUpDown, Cog, ShieldCheck, Home, HardHat, Hammer, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../lib/api';
import type { Report, ReportAttachment } from '../../types';
import { formatDate, formatDateTime, parseResidents } from '../../lib/format';
import {
  Spinner, EmptyState, StatusBadge, ConfirmDialog,
} from '../../components/ui';
import ReportAttachments from '../../components/ReportAttachments';
import DailyReportForm, { parseDailyReport } from '../../components/DailyReportForm';

function DailySectionView({ label, lines, attachmentIndices, attachments, onDeleted }: {
  label: string;
  lines: string[];
  attachmentIndices: number[];
  attachments: ReportAttachment[];
  onDeleted?: (id: number) => void;
}) {
  const [open, setOpen] = useState(true);
  const sectionAttachments = attachmentIndices.map((i) => attachments[i]).filter(Boolean);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
          {label}
          {lines.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />}
          {sectionAttachments.length > 0 && (
            <span className="text-xs text-gray-400">({sectionAttachments.length} file{sectionAttachments.length !== 1 ? 's' : ''})</span>
          )}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 py-3 bg-white">
          {lines.length > 0 ? (
            <div className="space-y-1 mb-3">
              {lines.map((line, i) => {
                const colonIdx = line.indexOf(':');
                if (colonIdx > 0 && colonIdx < 80) {
                  const key = line.slice(0, colonIdx).trim();
                  const val = line.slice(colonIdx + 1).trim();
                  return (
                    <div key={i} className="flex gap-2 text-sm">
                      <span className="font-medium text-gray-700 shrink-0">{key}:</span>
                      <span className="text-gray-600">{val}</span>
                    </div>
                  );
                }
                return <p key={i} className="text-sm text-gray-600">{line}</p>;
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic mb-3">No details provided.</p>
          )}
          {sectionAttachments.length > 0 && (
            <ReportAttachments attachments={sectionAttachments} onDeleted={onDeleted} />
          )}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [staffEstate, setStaffEstate] = useState('');

  const load = () => {
    setLoading(true);
    api
      .get<Report[]>('/reports')
      .then(setReports)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get<{ profile: { estate?: string } | null }>('/auth/profile').then((me) => {
      if (me.profile?.estate) setStaffEstate(me.profile.estate);
    }).catch(() => {});
  }, []);

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/reports/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  const removeAttachment = (reportId: number, attachmentId: number) => {
    setReports((prev) =>
      prev.map((r) => r.id === reportId
        ? { ...r, attachments: r.attachments.filter((a) => a.id !== attachmentId) }
        : r)
    );
  };

  const specialtyIcon = (r: Report) => {
    if (r.report_type === 'diesel') return <Fuel className="h-4 w-4 text-amber-600" />;
    if (r.report_type === 'ekedc') return <Zap className="h-4 w-4 text-orange-600" />;
    if (r.report_type === 'security_breach') return <ShieldAlert className="h-4 w-4 text-red-600" />;
    if (r.report_type === 'incident') return <Siren className="h-4 w-4 text-red-600" />;
    if (r.report_type === 'residents_complaint') return <Users className="h-4 w-4 text-primary" />;
    if (r.report_type === 'monthly_kpi') return <BarChart3 className="h-4 w-4 text-green-600" />;
    if (r.report_type === 'electrical') return <PlugZap className="h-4 w-4 text-amber-600" />;
    if (r.report_type === 'plumbing') return <Wrench className="h-4 w-4 text-primary" />;
    if (r.report_type === 'cleaning') return <Sparkles className="h-4 w-4 text-teal-600" />;
    if (r.report_type === 'lift') return <ArrowUpDown className="h-4 w-4 text-primary" />;
    if (r.report_type === 'generator') return <Cog className="h-4 w-4 text-lime-600" />;
    if (r.report_type === 'staff_report') return <HardHat className="h-4 w-4 text-primary" />;
    if (r.report_type === 'residents_report') return <Home className="h-4 w-4 text-violet-600" />;
    if (r.report_type === 'maintenance') return <Hammer className="h-4 w-4 text-yellow-600" />;
    if (r.report_type === 'security') return <ShieldCheck className="h-4 w-4 text-rose-600" />;
    return null;
  };

  const renderReport = (r: Report) => {
    const isDailyReport = r.report_type === 'daily' && r.heading === 'Daily Report';
    const sections = isDailyReport ? parseDailyReport(r.content, r.attachments) : null;

    return (
      <div key={r.id} className="bg-white p-5 rounded-lg shadow-lg">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={r.report_type} />
            {r.estate && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{r.estate}</span>
            )}
            {r.facility && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{r.facility}</span>
            )}
            {r.category && r.category !== 'general' && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 capitalize">{r.category}</span>
            )}
            <span className="text-sm text-gray-500">{formatDate(r.report_date)}</span>
          </div>
          <button onClick={() => setDeleteId(r.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        {r.heading && <h3 className="font-semibold text-gray-800 mb-1">{r.heading}</h3>}
        {(r.diesel_used_month !== null || r.ekedc_used_month !== null || r.diesel_liters_remaining !== null || r.ekedc_units_remaining !== null || r.severity || r.resident_name) && (
          <div className="flex flex-wrap items-center gap-3 mb-2 text-sm">
            {specialtyIcon(r)}
            {r.diesel_used_month !== null && (
              <span className="text-gray-700"><span className="font-medium">Diesel used this month:</span> {r.diesel_used_month} L</span>
            )}
            {r.ekedc_used_month !== null && (
              <span className="text-gray-700"><span className="font-medium">EKEDC used this month:</span> {r.ekedc_used_month} kWh</span>
            )}
            {r.diesel_liters_remaining !== null && (
              <span className="text-gray-700"><span className="font-medium">Diesel remaining:</span> {r.diesel_liters_remaining} L</span>
            )}
            {r.ekedc_units_remaining !== null && (
              <span className="text-gray-700"><span className="font-medium">EKEDC units remaining:</span> {r.ekedc_units_remaining} kWh</span>
            )}
            {r.resident_name && (() => {
              const residentEntries = parseResidents(r.resident_name, r.unit);
              return (
                <span className="text-gray-700">
                  <span className="font-medium">Resident:</span>{' '}
                  {residentEntries.length > 0 ? residentEntries.map((re) => `${re.name}${re.unit ? ` (${re.unit})` : ''}`).join(', ') : ''}
                </span>
              );
            })()}
            {r.severity && <StatusBadge status={r.severity} />}
            {r.security_location && <span className="text-gray-500 text-xs">{r.security_location}</span>}
          </div>
        )}
        <div className="flex items-center text-xs text-gray-400 mb-2">
          <Clock className="h-3.5 w-3.5 mr-1" />
          Uploaded {formatDateTime(r.created_at)}
        </div>

        {sections && sections.length > 0 ? (
          <div className="mt-3 space-y-2">
            {sections.map((sec) => (
              <DailySectionView
                key={sec.label}
                label={sec.label}
                lines={sec.lines}
                attachmentIndices={sec.attachmentIndices}
                attachments={r.attachments}
                onDeleted={(attId) => removeAttachment(r.id, attId)}
              />
            ))}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 whitespace-pre-line mb-3">{r.content}</p>
            {r.action_taken && (
              <p className="text-sm text-gray-600 mb-3"><span className="font-medium">Action taken:</span> {r.action_taken}</p>
            )}
            <ReportAttachments attachments={r.attachments} onDeleted={(attId) => removeAttachment(r.id, attId)} />
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Daily Report</h1>
        <button onClick={() => setModalOpen(true)} className="px-4 py-2 bg-primary text-black rounded-lg flex items-center space-x-2 hover:bg-primary-dark text-sm">
          <FilePlus2 className="h-4 w-4" /> New Report
        </button>
      </div>

      {loading ? (
        <Spinner label="Loading reports..." />
      ) : reports.length === 0 ? (
        <EmptyState message="No reports yet." />
      ) : (
        <div className="grid gap-4">
          {reports.map(renderReport)}
        </div>
      )}

      <DailyReportForm
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={load}
        estate={staffEstate}
      />

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Report"
        message="Are you sure you want to delete this report?"
      />
    </div>
  );
}
